import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { riseIn } from '@shared/theme/animations';
import { useAgentPermission } from '@entities/user';

/**
 * ОСНОВА.Логист — витрина модуля (каркас, ТЗ AI-LOGIST-2026).
 *
 * Электронные транспортные накладные (ЭТрН): прослойка между заказами/рейсами
 * САТКОМ и оператором ИС ЭПД. На текущем этапе — карточка агента с модулями по
 * волнам и пометкой «в разработке». Функциональный контур (справочники, заказы,
 * серии рейсов, весовой контроль, реестр ЭТрН) добавляется отдельными PR за
 * фичефлагом OSNOVA_LOGIST_ENABLED. Состав модулей зеркалит бэкенд `/agents/logist/info`.
 */

interface ModuleCard {
  id: string;
  wave: number;
  title: string;
  description: string;
}

const MODULES: ModuleCard[] = [
  {
    id: 'dictionaries',
    wave: 1,
    title: 'Справочники',
    description:
      'Водители, транспортные средства и прицепы; логистические атрибуты контрагентов поверх реестра Юриста (адреса погрузки/выгрузки, оператор ЭДО, роуминг).',
  },
  {
    id: 'orders',
    wave: 1,
    title: 'Заказы на перевозку',
    description:
      'Ручной ввод и импорт XLSX по шаблону (детект колонок по заголовкам), дедуп и идемпотентность, разбиение заказа на рейсы.',
  },
  {
    id: 'trips_series',
    wave: 1,
    title: 'Конвейер серий рейсов',
    description:
      'Однотипные ходки самосвалов на коротком плече: шаблон серии → генерация N черновиков ЭТрН одной операцией, дозапись фактической массы по каждой ходке, групповые операции.',
  },
  {
    id: 'weighing',
    wave: 1,
    title: 'Весовой контроль',
    description:
      'Записи взвешивания, три раздельных контура (нормативный / метрологический / административный риск), статусы PASSED / FAILED / NOT_CHECKED, блокировка выпуска при перегрузе. Арифметика — детерминированный движок.',
  },
  {
    id: 'etrn_registry',
    wave: 1,
    title: 'Реестр ЭТрН и обмен с оператором',
    description:
      'Черновики ЭТрН через API оператора (XML формирует оператор), реестр перевозок со сверкой статусов, timeline, блок «что мешает закрытию», доказательный архив.',
  },
  {
    id: 'incoming_llm',
    wave: 2,
    title: 'Входящий поток и LLM-слой',
    description:
      'Связка входящих ЭТрН (САТКОМ-грузополучатель) с поставками МТО и приёмкой Прораба; извлечение заявок из писем, объяснение ошибок оператора. LLM не создаёт и не изменяет юридически значимые данные.',
  },
];

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(6)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 820px;
`;

const DevBanner = styled.div`
  margin-top: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(4)}`};
  border: 1px dashed ${({ theme }) => theme.colors.borderStrong};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
  max-width: 820px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: ${({ theme }) => theme.spacing(4)};
`;

const Card = styled.div`
  padding: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;

const CardTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const WaveTag = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};
`;

const CardText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const NoAccess = styled.div`
  max-width: 640px;
  margin: ${({ theme }) => theme.spacing(10)} auto;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const QuickLinks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(4)};
`;

const QuickLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.pill};

  &:hover {
    border-color: ${({ theme }) => theme.colors.borderStrong};
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

export function LogistPage() {
  const { can_read } = useAgentPermission('logist');

  if (!can_read) {
    return (
      <NoAccess>
        Нет доступа к разделу «ОСНОВА.Логист». Обратитесь к администратору за правами.
      </NoAccess>
    );
  }

  return (
    <Wrap>
      <Header>
        <Title>
          ОСНОВА<span className="accent">.Логист</span>
        </Title>
        <Subtitle>
          Логистика / АТЦ: электронные транспортные накладные (ЭТрН). Прослойка между заказами и
          рейсами САТКОМ и оператором ИС ЭПД. С 01.09.2026 перевозочные документы по договорам
          перевозки оформляются только в электронном виде (140-ФЗ). Юридически значимые данные,
          подписи и статусы — от оператора; платформа не хранит и не обрабатывает закрытые ключи.
        </Subtitle>
        <DevBanner>
          Модуль в разработке. Ниже — состав по волнам ТЗ AI-LOGIST-2026. Функциональный контур
          подключается поэтапно за фичефлагом; юридический статус документа определяет только
          оператор ИС ЭПД.
        </DevBanner>
        <QuickLinks>
          <QuickLink to="/agents/logist/orders">Заказы на перевозку</QuickLink>
          <QuickLink to="/agents/logist/trips">Рейсы и серии</QuickLink>
          <QuickLink to="/agents/logist/registry">Реестр ЭТрН</QuickLink>
          <QuickLink to="/agents/logist/dictionaries">Справочники</QuickLink>
          <QuickLink to="/agents/logist/signers">Подписанты</QuickLink>
          <QuickLink to="/agents/logist/tasks">Задачи</QuickLink>
          <QuickLink to="/agents/logist/edo-boxes">Ящики ЭДО</QuickLink>
        </QuickLinks>
      </Header>
      <Grid>
        {MODULES.map((m) => (
          <Card key={m.id}>
            <CardHead>
              <CardTitle>{m.title}</CardTitle>
              <WaveTag>Волна {m.wave}</WaveTag>
            </CardHead>
            <CardText>{m.description}</CardText>
          </Card>
        ))}
      </Grid>
    </Wrap>
  );
}
